"use client";

import { useState, useEffect, useRef } from "react";
import {
  X,
  Save,
  Copy,
  Download,
  FileText,
  Edit3,
  Undo,
  Redo,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Indent,
  Outdent,
  Type,
  Palette,
  Minus,
  Plus,
  RotateCcw,
  Link,
  Image,
  Table,
  Search,
  Replace,
  Eye,
  EyeOff,
  Moon,
  Sun,
  ZoomIn,
  ZoomOut,
  Printer,
  FileImage,
  FilePlus,
  Folder,
  Settings,
  HelpCircle,
} from "lucide-react";

export default function NotepadEditorModal({ isOpen, onClose, soapNote }) {
  const [mode, setMode] = useState("soap"); // "soap" or "blank"
  const [activeTab, setActiveTab] = useState("subjective");
  const [editedContent, setEditedContent] = useState({
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
    blank: "",
  });
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Rich text editor states
  const [fontSize, setFontSize] = useState("14");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [textColor, setTextColor] = useState("#000000");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [textAlign, setTextAlign] = useState("left");
  const [lineHeight, setLineHeight] = useState("1.5");
  const [zoom, setZoom] = useState(100);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Search and replace
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");

  const editorRef = useRef(null);
  const searchInputRef = useRef(null);

  // Helper function to clean and format content
  const cleanAndFormatContent = (content) => {
    if (!content) return "";

    // Remove HTML tags
    let cleaned = content.replace(/<[^>]*>/g, "");

    // Clean up extra whitespace and line breaks
    cleaned = cleaned.replace(/\s+/g, " ").trim();

    // Add proper line breaks for better readability
    cleaned = cleaned.replace(/\. /g, ".\n");
    cleaned = cleaned.replace(/:\s*/g, ":\n  ");

    return cleaned;
  };

  // Initialize content when modal opens with soapNote data
  useEffect(() => {
    if (isOpen && soapNote) {
      setEditedContent({
        subjective: cleanAndFormatContent(soapNote.subjective) || "",
        objective: cleanAndFormatContent(soapNote.objective) || "",
        assessment: cleanAndFormatContent(soapNote.assessment) || "",
        plan: cleanAndFormatContent(soapNote.plan) || "",
        blank: "",
      });
      setMode("soap");
      setActiveTab("subjective");
    }
  }, [isOpen, soapNote]);

  // Initialize content
  useEffect(() => {
    const initialContent = {
      subjective: "",
      objective: "",
      assessment: "",
      plan: "",
      blank: "",
    };
    setEditedContent(initialContent);
    setHistory([initialContent]);
    setHistoryIndex(0);
  }, []);

  const handleContentChange = (section, value) => {
    const newContent = { ...editedContent, [section]: value };
    setEditedContent(newContent);
    setHasUnsavedChanges(true);

    // Add to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Rich text formatting functions
  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    updateFormattingState();
    setHasUnsavedChanges(true);
  };

  const updateFormattingState = () => {
    if (editorRef.current && document.activeElement === editorRef.current) {
      setIsBold(document.queryCommandState("bold"));
      setIsItalic(document.queryCommandState("italic"));
      setIsUnderline(document.queryCommandState("underline"));
      setIsStrikethrough(document.queryCommandState("strikeThrough"));
    }
  };

  const handleFontSizeChange = (size) => {
    setFontSize(size);
    if (editorRef.current) {
      editorRef.current.style.fontSize = `${size}px`;
    }
  };

  const handleFontFamilyChange = (family) => {
    setFontFamily(family);
    executeCommand("fontName", family);
  };

  const handleTextColorChange = (color) => {
    setTextColor(color);
    executeCommand("foreColor", color);
  };

  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);
    executeCommand("backColor", color);
  };

  const handleTextAlign = (align) => {
    setTextAlign(align);
    executeCommand("justify" + align.charAt(0).toUpperCase() + align.slice(1));
  };

  const handleIndent = (direction) => {
    if (direction === "increase") {
      executeCommand("indent");
    } else {
      executeCommand("outdent");
    }
  };

  const handleList = (type) => {
    if (type === "unordered") {
      executeCommand("insertUnorderedList");
    } else {
      executeCommand("insertOrderedList");
    }
  };

  const handleLineHeight = (height) => {
    setLineHeight(height);
    if (editorRef.current) {
      editorRef.current.style.lineHeight = height;
    }
  };

  const handleZoom = (direction) => {
    const newZoom =
      direction === "in" ? Math.min(zoom + 10, 200) : Math.max(zoom - 10, 50);
    setZoom(newZoom);
    if (editorRef.current) {
      editorRef.current.style.transform = `scale(${newZoom / 100})`;
      editorRef.current.style.transformOrigin = "top left";
    }
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setEditedContent(history[newIndex]);
      setHasUnsavedChanges(true);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setEditedContent(history[newIndex]);
      setHasUnsavedChanges(true);
    }
  };

  const handleInsertLink = () => {
    const url = prompt("Enter URL:");
    const text = prompt("Enter link text:");
    if (url && text) {
      executeCommand(
        "insertHTML",
        `<a href="${url}" target="_blank">${text}</a>`,
      );
    }
  };

  const handleInsertImage = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      executeCommand(
        "insertHTML",
        `<img src="${url}" style="max-width: 100%; height: auto;" alt="Inserted image" />`,
      );
    }
  };

  const handleInsertTable = () => {
    const rows = prompt("Number of rows:", "3");
    const cols = prompt("Number of columns:", "3");
    if (rows && cols) {
      let tableHTML =
        '<table border="1" style="border-collapse: collapse; width: 100%;">';
      for (let i = 0; i < parseInt(rows); i++) {
        tableHTML += "<tr>";
        for (let j = 0; j < parseInt(cols); j++) {
          tableHTML +=
            '<td style="padding: 8px; border: 1px solid #ccc;">&nbsp;</td>';
        }
        tableHTML += "</tr>";
      }
      tableHTML += "</table><br>";
      executeCommand("insertHTML", tableHTML);
    }
  };

  const handleSearch = () => {
    if (searchTerm && editorRef.current) {
      const content = editorRef.current.innerHTML;
      const highlightedContent = content.replace(
        new RegExp(searchTerm, "gi"),
        `<mark style="background-color: yellow;">${searchTerm}</mark>`,
      );
      editorRef.current.innerHTML = highlightedContent;
    }
  };

  const handleReplace = () => {
    if (searchTerm && replaceTerm && editorRef.current) {
      const content = editorRef.current.innerHTML;
      const replacedContent = content.replace(
        new RegExp(searchTerm, "gi"),
        replaceTerm,
      );
      editorRef.current.innerHTML = replacedContent;
      const currentSection = mode === "blank" ? "blank" : activeTab;
      handleContentChange(currentSection, replacedContent);
    }
  };

  const handleSave = () => {
    console.log("Saving notepad content:", editedContent);
    setHasUnsavedChanges(false);
  };

  const handleCopy = async () => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection.toString()) {
        // Copy selected text
        await navigator.clipboard.writeText(selection.toString());
        console.log("Selected text copied to clipboard");
      } else {
        // Copy all formatted content
        if (mode === "soap") {
          const formattedContent = `SUBJECTIVE:
${editedContent.subjective || "No content"}

OBJECTIVE:
${editedContent.objective || "No content"}

ASSESSMENT:
${editedContent.assessment || "No content"}

PLAN:
${editedContent.plan || "No content"}`;

          await navigator.clipboard.writeText(formattedContent);
          console.log("All SOAP content copied to clipboard");
        } else {
          // Copy blank content
          await navigator.clipboard.writeText(editedContent.blank || "");
          console.log("Blank content copied to clipboard");
        }
      }
    }
  };

  const handleDownload = (format = "txt") => {
    let content = "";
    let filename = "";
    let mimeType = "";

    if (mode === "soap") {
      content = `SOAP NOTE - Advanced Editor
Generated: ${new Date().toLocaleDateString()}

SUBJECTIVE:
${editedContent.subjective?.replace(/<[^>]*>/g, "") || ""}

OBJECTIVE:
${editedContent.objective?.replace(/<[^>]*>/g, "") || ""}

ASSESSMENT:
${editedContent.assessment?.replace(/<[^>]*>/g, "") || ""}

PLAN:
${editedContent.plan?.replace(/<[^>]*>/g, "") || ""}`;
      filename = `soap-note-${new Date().toISOString().split("T")[0]}.${format}`;
    } else {
      content = editedContent.blank?.replace(/<[^>]*>/g, "") || "";
      filename = `notes-${new Date().toISOString().split("T")[0]}.${format}`;
    }

    if (format === "html") {
      const htmlContent =
        mode === "soap"
          ? `<!DOCTYPE html>
<html>
<head>
    <title>SOAP Note - ${new Date().toLocaleDateString()}</title>
    <style>
        body { font-family: ${fontFamily}; font-size: ${fontSize}px; line-height: ${lineHeight}; }
        .section { margin-bottom: 20px; }
        .section-title { font-weight: bold; margin-bottom: 10px; }
    </style>
</head>
<body>
    <h1>SOAP Note - Advanced Editor</h1>
    <p>Generated: ${new Date().toLocaleDateString()}</p>
    <div class="section">
        <div class="section-title">SUBJECTIVE:</div>
        <div>${editedContent.subjective}</div>
    </div>
    <div class="section">
        <div class="section-title">OBJECTIVE:</div>
        <div>${editedContent.objective}</div>
    </div>
    <div class="section">
        <div class="section-title">ASSESSMENT:</div>
        <div>${editedContent.assessment}</div>
    </div>
    <div class="section">
        <div class="section-title">PLAN:</div>
        <div>${editedContent.plan}</div>
    </div>
</body>
</html>`
          : `<!DOCTYPE html>
<html>
<head>
    <title>Notes - ${new Date().toLocaleDateString()}</title>
    <style>
        body { font-family: ${fontFamily}; font-size: ${fontSize}px; line-height: ${lineHeight}; }
    </style>
</head>
<body>
    ${editedContent.blank}
</body>
</html>`;
      content = htmlContent;
      mimeType = "text/html";
    } else {
      mimeType = "text/plain";
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getCurrentContent = () => {
    return mode === "blank" ? editedContent.blank : editedContent[activeTab];
  };

  const setCurrentContent = (value) => {
    const section = mode === "blank" ? "blank" : activeTab;
    handleContentChange(section, value);
  };

  const tabs = [
    { id: "subjective", label: "Subjective", color: "text-blue-600" },
    { id: "objective", label: "Objective", color: "text-green-600" },
    { id: "assessment", label: "Assessment", color: "text-orange-600" },
    { id: "plan", label: "Plan", color: "text-purple-600" },
  ];

  // If not open, don't render anything
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Advanced Modal */}
      {isOpen && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${isFullScreen ? "p-0" : ""}`}
        >
          <div
            className={`bg-white rounded-lg shadow-xl flex flex-col ${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"} ${
              isFullScreen
                ? "w-full h-full rounded-none"
                : "w-full max-w-7xl h-[95vh] mx-2 md:mx-auto"
            }`}
          >
            {/* Header */}
            <div
              className={`flex items-center justify-between p-4 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {mode === "soap"
                      ? "SOAP Notes Editor"
                      : "Blank Notes Editor"}
                  </h2>
                  {hasUnsavedChanges && (
                    <span className="text-sm text-orange-600 font-medium">
                      • Unsaved changes
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                >
                  {isDarkMode ? (
                    <Sun className="w-4 h-4" />
                  ) : (
                    <Moon className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                >
                  {isFullScreen ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => handleDownload("txt")}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges}
                  className={`p-2 rounded-lg transition-colors ${hasUnsavedChanges ? (isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100") : "opacity-50 cursor-not-allowed"}`}
                >
                  <Save className="w-4 h-4" />
                </button>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* SOAP Sections - Horizontal tabs for all screen sizes (mobile/tablet/desktop) */}
              {mode === "soap" && (
                <div
                  className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"} px-2 sm:px-4 py-2`}
                >
                  <div className="flex flex-wrap items-center gap-x-1 gap-y-2 sm:gap-x-2 md:gap-x-3">
                    {tabs.map((tab, idx) => (
                      <div key={tab.id} className="flex items-center">
                        <button
                          onClick={() => setActiveTab(tab.id)}
                          className={`
                            group flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-sm font-medium whitespace-nowrap
                            ${
                              activeTab === tab.id
                                ? isDarkMode
                                  ? "bg-gray-700 text-white shadow-sm"
                                  : "bg-white shadow-sm border border-gray-200 text-gray-900"
                                : isDarkMode
                                  ? "hover:bg-gray-700/50 text-gray-300"
                                  : "hover:bg-gray-100 text-gray-700"
                            }
                          `}
                        >
                          <span
                            className={
                              activeTab === tab.id ? tab.color : undefined
                            }
                          >
                            {tab.label}
                          </span>
                          <span
                            className={`text-xs sm:text-xs ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            (
                            {
                              (
                                editedContent[tab.id]?.replace(
                                  /<[^>]*>/g,
                                  "",
                                ) || ""
                              ).length
                            }
                            )
                          </span>
                        </button>
                        {/* Pipe separator between tabs, hidden after last tab */}
                        {idx < tabs.length - 1 && (
                          <span
                            className={`hidden sm:inline mx-0 sm:mx-1 select-none ${
                              isDarkMode ? "text-gray-600" : "text-gray-300"
                            }`}
                          >
                            |
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Main Editor Area (shared between mobile and desktop, also for blank mode) */}
              <div className="flex-1 flex flex-col">
                {/* Toolbar */}
                <div
                  className={`border-b p-3 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"}`}
                >
                  {/* First Row - File Operations */}
                  <div className="flex flex-wrap items-center gap-1 md:gap-2 mb-3 pb-2 border-b border-gray-300">
                    <select
                      value={fontFamily}
                      onChange={(e) => handleFontFamilyChange(e.target.value)}
                      className={`px-2 md:px-3 py-1 border rounded text-sm ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"}`}
                    >
                      <option value="Arial">Arial</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Courier New">Courier New</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Verdana">Verdana</option>
                      <option value="Helvetica">Helvetica</option>
                    </select>

                    <select
                      value={fontSize}
                      onChange={(e) => handleFontSizeChange(e.target.value)}
                      className={`px-2 md:px-3 py-1 border rounded text-sm w-16 md:w-20 ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"}`}
                    >
                      {[8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32].map(
                        (size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ),
                      )}
                    </select>

                    <div className="flex items-center space-x-1 border-l border-gray-300 pl-2">
                      <button
                        onClick={() => executeCommand("bold")}
                        className={`p-1 md:p-2 rounded transition-colors ${
                          isBold
                            ? "bg-blue-600 text-white"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <Bold className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => executeCommand("italic")}
                        className={`p-1 md:p-2 rounded transition-colors ${
                          isItalic
                            ? "bg-blue-600 text-white"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <Italic className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => executeCommand("underline")}
                        className={`p-1 md:p-2 rounded transition-colors ${
                          isUnderline
                            ? "bg-blue-600 text-white"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <Underline className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => executeCommand("strikeThrough")}
                        className={`p-1 md:p-2 rounded transition-colors ${
                          isStrikethrough
                            ? "bg-blue-600 text-white"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <Strikethrough className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center space-x-1 border-l border-gray-300 pl-2">
                      <button
                        onClick={() => handleTextAlign("left")}
                        className={`p-1 md:p-2 rounded transition-colors ${
                          textAlign === "left"
                            ? "bg-blue-600 text-white"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <AlignLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleTextAlign("center")}
                        className={`p-1 md:p-2 rounded transition-colors ${
                          textAlign === "center"
                            ? "bg-blue-600 text-white"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <AlignCenter className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleTextAlign("right")}
                        className={`p-1 md:p-2 rounded transition-colors ${
                          textAlign === "right"
                            ? "bg-blue-600 text-white"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <AlignRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleTextAlign("justify")}
                        className={`p-1 md:p-2 rounded transition-colors ${
                          textAlign === "justify"
                            ? "bg-blue-600 text-white"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <AlignJustify className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center space-x-1 border-l border-gray-300 pl-2">
                      <button
                        onClick={() => handleList("unordered")}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                      >
                        <List className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleList("ordered")}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                      >
                        <ListOrdered className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleIndent("increase")}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                      >
                        <Indent className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleIndent("decrease")}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                      >
                        <Outdent className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center space-x-2 border-l border-gray-300 pl-2">
                      <input
                        type="color"
                        value={textColor}
                        onChange={(e) => handleTextColorChange(e.target.value)}
                        className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                        title="Text Color"
                      />
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) =>
                          handleBackgroundColorChange(e.target.value)
                        }
                        className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                        title="Background Color"
                      />
                    </div>

                    <div className="flex items-center space-x-1 border-l border-gray-300 pl-2">
                      <button
                        onClick={handleInsertLink}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                        title="Insert Link"
                      >
                        <Link className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleInsertImage}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                        title="Insert Image"
                      >
                        <Image className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleInsertTable}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                        title="Insert Table"
                      >
                        <Table className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center space-x-1 border-l border-gray-300 pl-2">
                      <button
                        onClick={() => handleZoom("out")}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </button>
                      <span className="text-sm px-2">{zoom}%</span>
                      <button
                        onClick={() => handleZoom("in")}
                        className={`p-1 md:p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                        title="Zoom In"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Second Row - Editor Actions */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center space-x-2">
                      {mode === "soap" && (
                        <>
                          <h3 className="text-lg font-medium hidden md:block">
                            {tabs.find((tab) => tab.id === activeTab)?.label}
                          </h3>
                          <span
                            className={`text-sm font-medium ${
                              tabs.find((tab) => tab.id === activeTab)?.color
                            } hidden md:inline`}
                          >
                            (
                            {
                              (
                                getCurrentContent()?.replace(/<[^>]*>/g, "") ||
                                ""
                              ).length
                            }{" "}
                            characters)
                          </span>
                        </>
                      )}
                      {mode === "blank" && (
                        <>
                          <h3 className="text-lg font-medium">Notes</h3>
                          <span className="text-sm font-medium text-blue-600">
                            (
                            {
                              (
                                getCurrentContent()?.replace(/<[^>]*>/g, "") ||
                                ""
                              ).length
                            }{" "}
                            characters)
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowSearch(!showSearch)}
                        className={`p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                        title="Search & Replace"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleUndo}
                        disabled={historyIndex <= 0}
                        className={`p-2 rounded transition-colors ${
                          historyIndex <= 0
                            ? "opacity-50 cursor-not-allowed"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <Undo className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleRedo}
                        disabled={historyIndex >= history.length - 1}
                        className={`p-2 rounded transition-colors ${
                          historyIndex >= history.length - 1
                            ? "opacity-50 cursor-not-allowed"
                            : isDarkMode
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-200"
                        }`}
                      >
                        <Redo className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCopy}
                        title="Copy All SOAP Content"
                        className={`p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Search Bar */}
                {showSearch && (
                  <div
                    className={`p-3 border-b ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"}`}
                  >
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded text-sm ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"}`}
                      />
                      <input
                        type="text"
                        placeholder="Replace..."
                        value={replaceTerm}
                        onChange={(e) => setReplaceTerm(e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded text-sm ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"}`}
                      />
                      <button
                        onClick={handleSearch}
                        className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Find
                      </button>
                      <button
                        onClick={handleReplace}
                        className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                      >
                        Replace
                      </button>
                    </div>
                  </div>
                )}

                {/* Rich Text Editor */}
                <div className="flex-1 p-4 overflow-auto">
                  <textarea
                    ref={editorRef}
                    value={getCurrentContent() || ""}
                    onChange={(e) => setCurrentContent(e.target.value)}
                    className={`w-full h-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-y-auto ${
                      isDarkMode
                        ? "border-gray-600 bg-gray-800 text-white"
                        : "border-gray-300 bg-white text-gray-900"
                    }`}
                    style={{
                      minHeight: "500px",
                      fontFamily: fontFamily,
                      fontSize: `${fontSize}px`,
                      lineHeight: lineHeight,
                      color: textColor,
                      backgroundColor: backgroundColor,
                      transform: `scale(${zoom / 100})`,
                      transformOrigin: "top left",
                      width: `${100 / (zoom / 100)}%`, // counteract scale width
                    }}
                    placeholder={
                      mode === "soap"
                        ? `Enter ${tabs.find((tab) => tab.id === activeTab)?.label.toLowerCase()} information...`
                        : "Start writing your notes..."
                    }
                  />
                </div>

                {/* Footer with statistics and export options */}
                <div
                  className={`border-t p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"}`}
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm">
                    <div className="flex flex-wrap items-center gap-3">
                      <div>
                        Words:{" "}
                        {(() => {
                          const textContent =
                            getCurrentContent()?.replace(/<[^>]*>/g, "") || "";
                          return textContent
                            .split(/\s+/)
                            .filter((word) => word.length > 0).length;
                        })()}
                      </div>
                      <div>
                        Characters:{" "}
                        {(() => {
                          const textContent =
                            getCurrentContent()?.replace(/<[^>]*>/g, "") || "";
                          return textContent.length;
                        })()}
                      </div>
                      <div>
                        Paragraphs:{" "}
                        {(() => {
                          const textContent =
                            getCurrentContent()?.replace(/<[^>]*>/g, "") || "";
                          return textContent
                            .split(/\n\s*\n/)
                            .filter((p) => p.trim().length > 0).length;
                        })()}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => handleDownload("txt")}
                        className={`px-3 py-1 text-sm border rounded transition-colors ${isDarkMode ? "border-gray-600 hover:bg-gray-700" : "border-gray-300 hover:bg-gray-100"}`}
                      >
                        Export TXT
                      </button>
                      <button
                        onClick={() => handleDownload("html")}
                        className={`px-3 py-1 text-sm border rounded transition-colors ${isDarkMode ? "border-gray-600 hover:bg-gray-700" : "border-gray-300 hover:bg-gray-100"}`}
                      >
                        Export HTML
                      </button>
                      <div
                        className={`text-sm px-3 py-1 rounded ${
                          hasUnsavedChanges
                            ? "text-orange-600 font-medium"
                            : "text-green-600 font-medium"
                        }`}
                      >
                        {hasUnsavedChanges
                          ? "Unsaved changes"
                          : "All changes saved"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}